import React from "react";
import { FormattedMessage } from "react-intl";

import Button from "@components/button/button";
import CoinGreySvg from "@components/icons/coin-grey-svg";
import CoinSvg from "@components/icons/coin-svg";
import DoneSvg from "@components/icons/done-svg";
import QueryHandler from "@components/query-handler/query-handler";

import { useAppDispatch, useAppSelector } from "@hooks/use-store";
import { setActiveCharacterName } from "@store/app/app.slice";
import { formatUrlWithLocale } from "@helpers/format-url-with-locale";
import { CharactersTypes } from "@typings/global.types";

import { useUpdateCharactersMutation } from "../../api/characters";
import { useUpdateUserInfoMutation } from "../../api/user-info";

type PropsTypeCharacterShopCard = {
  children: React.ReactNode;
  name: CharactersTypes | null;
  typeBtn?: string;
  id: string;
  coins?: number;
  paymentForm?: string;
  clearUpdate?: () => void;
};

function CharacterShopCard({
  children,
  name,
  typeBtn,
  id,
  coins,
  paymentForm,
  clearUpdate,
}: PropsTypeCharacterShopCard) {
  const { userData } = useAppSelector((state) => state.app);

  const dispatch = useAppDispatch();

  const [editUser, { isError, error }] = useUpdateUserInfoMutation();
  const [
    updateCharacters,
    {
      isSuccess: charactersIsSuccess,
      isError: charactersIsError,
      error: charactersError,
      isLoading,
    },
  ] = useUpdateCharactersMutation();

  const handleChangeCharacter = (name: CharactersTypes | null, id: number) => {
    dispatch(setActiveCharacterName(name));
    if (!userData.gameMode) {
      editUser({
        params: {
          gameMode: true,
        },
      });
    }
    updateCharacters({
      params: {
        characterId: Number(id),
      },
    });
    if (clearUpdate) {
      clearUpdate();
    }
  };

  const renderButton = (
    id: string,
    type: string | undefined,
    name: CharactersTypes | null,
  ): React.ReactNode => {
    switch (type) {
      case "purchased":
        return (
          <Button
            onClick={() => handleChangeCharacter(name, Number(id))}
            id={id}
            className='btn btn-secondary'
            disabled={isLoading}
          >
            <FormattedMessage id='select' />
          </Button>
        );
      case "selected":
        return (
          <DoneSvg
            classNameProps='mx-auto mt-[1.06rem] md:mt-4'
            width={31}
            height={30}
            colorFill='fill-green'
          />
        );
      case "disabled":
        return (
          <Button
            className='btn btn-primary btn-with-icon dark:disabled:bg-bg-dark-secondary dark:disabled:text-grey-dark-dark dark:disabled:border-grey-dark-default mx-auto disabled:translate-y-0 disabled:cursor-not-allowed disabled:border-grey disabled:bg-bg-secondary disabled:text-grey-dark disabled:before:shadow-grey disabled:active:before:shadow-grey dark:disabled:before:shadow-grey'
            disabled
          >
            <CoinGreySvg width={23} height={27} />
            {coins}
          </Button>
        );
      case "currencyCost":
        if (id === "13") {
          return (
            <a
              className='btn btn-quinary'
              id='GetHero_btn_tutor'
              href={formatUrlWithLocale("/ratatype-plus/")}
              onClick={(e) => {
                document.cookie = `eventCategory=${e.currentTarget.id};path=/`;
              }}
            >
              <FormattedMessage id='get' />
            </a>
          );
        }
        return (
          <span
            dangerouslySetInnerHTML={{
              __html: `${paymentForm}`,
            }}
          />
        );

      default:
        return (
          <Button
            onClick={() => handleChangeCharacter(name, Number(id))}
            id={id}
            className='btn btn-primary btn-with-icon mx-auto'
          >
            <CoinSvg width={27} height={27} />
            {coins}
          </Button>
        );
    }
  };

  return (
    <QueryHandler
      isError={charactersIsError || isError}
      errorMessage={charactersError || error}
    >
      <div className='mt-4 w-3/6 text-center md:mt-6 md:w-2/6'>
        {children}
        <div className='mb-2 font-bold capitalize'>
          <FormattedMessage id={name as string} />
        </div>
        {renderButton(id, typeBtn, name)}
      </div>
    </QueryHandler>
  );
}

export default CharacterShopCard;
